/*
  <tabletop-diagram> — S7 custom tabletop-dimensions visual (DESK-ONLY).

  Selectable cards (one per blat size) that BOTH reflect AND control the buy-box
  size variant. There is exactly ONE owner of variant state: the native
  <variant-selects> form. This element never stores its own variant — it:

    READS  : subscribes to PUB_SUB_EVENTS.variantChange and highlights the card
             matching the freshly-selected variant's size option value.
    WRITES : on card click, finds the native size option input
             (radio / <select>) inside <variant-selects> and dispatches a
             bubbling 'change' on it, which round-trips through product-info.js.

  Matching is by the option VALUE label (set server-side as data-size-value on
  each card) against the variant's option values, so it works regardless of the
  option's position (Culoare / Dimensiune order may differ per product).

  No animation here beyond CSS class toggles, so prefers-reduced-motion needs no
  JS gating — but the CSS still gates its transitions.
*/
if (!customElements.get('tabletop-diagram')) {
  customElements.define(
    'tabletop-diagram',
    class TabletopDiagram extends HTMLElement {
      connectedCallback() {
        // The product section we belong to (so we only react to OUR product's
        // variant changes, never another section on the page).
        this.sectionId = this.dataset.productSectionId || '';
        // Lower-cased keyword(s) that identify the size option by name.
        this.optionKey = (this.dataset.optionKey || 'dimensiune,blat,size').toLowerCase();

        this.cards = Array.from(this.querySelectorAll('[data-tabletop-card]'));
        if (!this.cards.length) return;

        this.onCardClick = this.onCardClick.bind(this);
        this.onCardKey = this.onCardKey.bind(this);
        this.cards.forEach((card) => {
          card.addEventListener('click', this.onCardClick);
          card.addEventListener('keydown', this.onCardKey);
        });

        // Subscribe to the single source of truth. `subscribe` / PUB_SUB_EVENTS
        // are globals from pubsub.js + constants.js (both loaded defer in head).
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data;
            if (!data) return;
            // Only honour changes from our own product section.
            if (this.sectionId && data.sectionId && data.sectionId !== this.sectionId) return;
            this.syncFromVariant(data.variant);
          });
        }

        // Initial highlight from whatever the buy box already has selected,
        // so the cards match the page state before any interaction.
        this.syncFromSelected();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        this.cards.forEach((card) => {
          card.removeEventListener('click', this.onCardClick);
          card.removeEventListener('keydown', this.onCardKey);
        });
      }

      /* ---- the owning <variant-selects> form ---- */
      get variantSelects() {
        if (!this.sectionId) return null;
        return document.getElementById('variant-selects-' + this.sectionId);
      }

      /* Find the native control (radio set or <select>) for the size option,
         matched by its option name containing one of the configured keywords. */
      getSizeControl() {
        const vs = this.variantSelects;
        if (!vs) return null;
        const keys = this.optionKey.split(',').map((k) => k.trim()).filter(Boolean);
        const matchesKey = (name) => {
          const n = (name || '').toLowerCase();
          return keys.some((k) => n.indexOf(k) !== -1);
        };

        // 1) <select name="options[Dimensiune blat]">
        const selects = vs.querySelectorAll('select[name^="options"]');
        for (const sel of selects) {
          const m = sel.name.match(/options\[(.*)\]/);
          if (m && matchesKey(m[1])) return { type: 'select', el: sel };
        }

        // 2) radio fieldset — legend text identifies the option.
        const fieldsets = vs.querySelectorAll('fieldset');
        for (const fs of fieldsets) {
          const legend = fs.querySelector('legend, .form__label');
          if (legend && matchesKey(legend.textContent)) {
            const radios = fs.querySelectorAll('input[type="radio"]');
            if (radios.length) return { type: 'radio', el: fs, radios };
          }
        }

        // 3) Single-option products: fall back to the only control present.
        if (selects.length === 1) return { type: 'select', el: selects[0] };
        const onlyFieldset = vs.querySelector('fieldset');
        if (onlyFieldset) {
          const radios = onlyFieldset.querySelectorAll('input[type="radio"]');
          if (radios.length) return { type: 'radio', el: onlyFieldset, radios };
        }
        return null;
      }

      /* ---- WRITE: click a card -> drive the native size input ---- */
      onCardClick(e) {
        const card = e.currentTarget;
        const value = card.dataset.sizeValue;
        if (value == null) return;
        this.selectSize(value);
      }

      onCardKey(e) {
        // Cards are <button>s so Enter/Space already fire click; this is a
        // belt-and-braces guard for non-button hosts.
        if (e.target.tagName === 'BUTTON') return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.onCardClick(e);
        }
      }

      selectSize(value) {
        const control = this.getSizeControl();
        const target = (value || '').trim().toLowerCase();
        if (!control) {
          // No native picker (e.g. single-variant product): just reflect locally.
          this.highlight(value);
          return;
        }

        if (control.type === 'select') {
          const opt = Array.from(control.el.options).find(
            (o) => (o.value || o.textContent || '').trim().toLowerCase() === target
          );
          if (!opt || control.el.value === opt.value) {
            // Already selected (or not found) — still reflect highlight.
            this.highlight(value);
            return;
          }
          control.el.value = opt.value;
          control.el.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (control.type === 'radio') {
          const radio = Array.from(control.radios).find(
            (r) => (r.value || '').trim().toLowerCase() === target
          );
          if (!radio || radio.checked) {
            this.highlight(value);
            return;
          }
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // The variantChange that follows will call syncFromVariant -> highlight.
      }

      /* ---- READ: reflect the active variant ---- */
      syncFromVariant(variant) {
        if (!variant) return;
        // variant.options is an ordered array of the chosen values; match any
        // of them to a card's size value (the size value is one of them).
        const values = Array.isArray(variant.options) ? variant.options : [];
        const norm = values.map((v) => (v || '').trim().toLowerCase());
        const card = this.cards.find((c) =>
          norm.indexOf((c.dataset.sizeValue || '').trim().toLowerCase()) !== -1
        );
        if (card) this.highlight(card.dataset.sizeValue);
      }

      /* Reflect whatever the native picker currently has selected (no event). */
      syncFromSelected() {
        const control = this.getSizeControl();
        if (!control) return;
        let current = '';
        if (control.type === 'select') {
          current = control.el.value;
        } else if (control.type === 'radio') {
          const checked = Array.from(control.radios).find((r) => r.checked);
          current = checked ? checked.value : '';
        }
        if (current) this.highlight(current);
      }

      highlight(value) {
        const target = (value || '').trim().toLowerCase();
        this.cards.forEach((card) => {
          const active = (card.dataset.sizeValue || '').trim().toLowerCase() === target;
          card.classList.toggle('is-active', active);
          card.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
    }
  );
}
